import React from "react";
import { getBrand } from "../../lib/productBrand";

/**
 * ProductLabel — replicates the printed Andrich catalog styling per product.
 * Layout variants: capsule, capsule-split, rounded, rounded-split, script, banner
 */
export default function ProductLabel({ name, form, className = "" }) {
  const b = getBrand(name);

  return (
    <div
      className={`relative w-full h-full overflow-hidden bg-white ${className}`}
      aria-label={`${name} label`}
    >
      {/* Ambient background */}
      <div
        className="absolute inset-0"
        style={{
          background: `
            radial-gradient(120% 80% at 100% 0%, ${withAlpha(b.accent, 0.18)} 0%, transparent 55%),
            radial-gradient(120% 80% at 0% 100%, ${withAlpha(b.bg, 0.08)} 0%, transparent 55%),
            #ffffff`,
        }}
      />
      {/* Dot texture */}
      <div
        className="absolute inset-0 opacity-40"
        style={{
          backgroundImage: `radial-gradient(${withAlpha(b.accent, 0.22)} 1px, transparent 1px)`,
          backgroundSize: "14px 14px",
        }}
      />
      {/* Accent bar */}
      <div
        className="absolute top-0 left-0 h-1.5 w-2/3"
        style={{ background: `linear-gradient(90deg, ${b.bg2 || b.bg}, ${b.accent})` }}
      />

      <div className="absolute inset-0 flex items-center justify-center px-4">
        <LabelBody brand={b} form={form} />
      </div>

      {/* Bottom composition strip */}
      <div className="absolute bottom-0 inset-x-0 h-6 flex items-center justify-center">
        <span className="text-[0.5rem] tracking-[0.24em] uppercase text-slate-400 font-medium">
          Andrich Pharmaceuticals
        </span>
      </div>
    </div>
  );
}

function LabelBody({ brand, form }) {
  const badge = brand.badge || form || "";
  switch (brand.layout) {
    case "capsule":
      return <Capsule brand={brand} badge={badge} />;
    case "capsule-split":
      return <CapsuleSplit brand={brand} badge={badge} />;
    case "rounded":
      return <Rounded brand={brand} badge={badge} />;
    case "rounded-split":
      return <RoundedSplit brand={brand} badge={badge} />;
    case "script":
      return <Script brand={brand} badge={badge} />;
    case "banner":
      return <Banner brand={brand} badge={badge} />;
    default:
      return <Rounded brand={brand} badge={badge} />;
  }
}

/* ---------- Layout variants ---------- */

function Capsule({ brand, badge }) {
  return (
    <div
      className="flex items-center justify-between gap-3 px-6 py-3 shadow-lg w-full"
      style={{
        background: brand.bg,
        color: brand.text,
        borderRadius: "9999px",
      }}
    >
      <TitleText brand={brand} />
      {badge && <FormBadge brand={brand} label={badge} />}
    </div>
  );
}

function CapsuleSplit({ brand, badge }) {
  return (
    <div
      className="flex items-stretch shadow-lg w-full overflow-hidden"
      style={{ borderRadius: "9999px" }}
    >
      <div
        className="flex-1 flex items-center justify-center px-5 py-3"
        style={{ background: brand.bg, color: brand.text }}
      >
        <span
          className={`font-display leading-none tracking-tight ${brand.italic ? "italic" : ""} ${brand.primaryBold === false ? "font-semibold" : "font-bold"}`}
          style={{ fontSize: fontSizeFor(brand.primary) }}
        >
          {brand.primary}
        </span>
      </div>
      {brand.suffix && (
        <div
          className="px-4 flex items-center justify-center"
          style={{ background: brand.bg2 || darken(brand.bg), color: brand.text2 || brand.text }}
        >
          <span className="font-display font-bold leading-none tracking-tight" style={{ fontSize: "1.4rem" }}>
            {brand.suffix}
          </span>
        </div>
      )}
    </div>
  );
}

function Rounded({ brand, badge }) {
  const bgStyle = brand.bg2
    ? { background: `linear-gradient(135deg, ${brand.bg} 0%, ${brand.bg2} 100%)` }
    : { background: brand.bg };
  return (
    <div
      className="flex items-center justify-between gap-3 px-5 py-3 shadow-lg w-full"
      style={{ ...bgStyle, color: brand.text, borderRadius: "14px" }}
    >
      <div className="flex items-baseline gap-1.5 min-w-0">
        <span
          className={`font-display leading-none tracking-tight ${brand.italic ? "italic" : ""} font-bold`}
          style={{ fontSize: fontSizeFor(brand.primary) }}
        >
          {brand.primary}
        </span>
        {brand.suffix && (
          <span
            className={`font-display leading-none ${brand.italic ? "italic" : ""} font-semibold`}
            style={{ fontSize: "0.95rem", color: brand.accent }}
          >
            {brand.suffix}
          </span>
        )}
      </div>
      {badge && <FormBadge brand={brand} label={badge} />}
    </div>
  );
}

function RoundedSplit({ brand, badge }) {
  return (
    <div className="flex items-stretch w-full shadow-lg overflow-hidden" style={{ borderRadius: "14px" }}>
      <div
        className="flex-1 flex items-center justify-center px-5 py-3"
        style={{ background: brand.bg, color: brand.text }}
      >
        <span
          className={`font-display leading-none tracking-tight ${brand.italic ? "italic" : ""} font-bold`}
          style={{ fontSize: fontSizeFor(brand.primary) }}
        >
          {brand.primary}
        </span>
      </div>
      {brand.suffix && (
        <div
          className="px-4 flex items-center justify-center"
          style={{ background: brand.bg2 || darken(brand.bg), color: brand.text2 || "#ffffff" }}
        >
          <span className="font-display font-bold leading-none tracking-tight" style={{ fontSize: "1.25rem" }}>
            {brand.suffix}
          </span>
        </div>
      )}
    </div>
  );
}

function Script({ brand, badge }) {
  return (
    <div className="flex flex-col items-center text-center gap-1.5 w-full px-4">
      <div className="flex items-baseline gap-2 flex-wrap justify-center">
        <span
          className="font-display italic font-bold leading-none tracking-tight"
          style={{ color: brand.text, fontSize: fontSizeFor(brand.primary, +0.15) }}
        >
          {brand.primary}
        </span>
        {brand.suffix && (
          <span
            className="font-display italic font-semibold leading-none"
            style={{ color: brand.accent, fontSize: "1rem" }}
          >
            {brand.suffix}
          </span>
        )}
      </div>
      {badge && (
        <span
          className="text-[0.6rem] font-semibold uppercase tracking-widest px-2.5 py-0.5 rounded"
          style={{ background: withAlpha(brand.accent, 0.15), color: brand.text }}
        >
          {badge}
        </span>
      )}
    </div>
  );
}

function Banner({ brand, badge }) {
  return (
    <div className="flex flex-col items-center text-center gap-1.5 w-full px-4">
      <div className="flex items-baseline gap-2 flex-wrap justify-center">
        <span
          className="font-display font-bold leading-none tracking-tight"
          style={{ color: brand.text, fontSize: fontSizeFor(brand.primary, +0.15) }}
        >
          {brand.primary}
        </span>
        {brand.suffix && (
          <span
            className="font-display font-semibold leading-none"
            style={{ color: brand.text2 || brand.accent, fontSize: "1rem" }}
          >
            {brand.suffix}
          </span>
        )}
      </div>
      {/* Underline flourish */}
      <div
        className="h-[3px] w-16 rounded-full"
        style={{ background: `linear-gradient(90deg, ${brand.text}, ${brand.accent})` }}
      />
      {badge && (
        <span
          className="text-[0.6rem] font-semibold uppercase tracking-widest px-2.5 py-0.5 rounded"
          style={{ background: withAlpha(brand.accent, 0.15), color: brand.text }}
        >
          {badge}
        </span>
      )}
    </div>
  );
}

/* ---------- Building blocks ---------- */

function TitleText({ brand }) {
  return (
    <span
      className={`font-display leading-none tracking-tight ${brand.italic ? "italic" : ""} font-bold`}
      style={{ color: brand.text, fontSize: fontSizeFor(brand.primary) }}
    >
      {brand.primary}
      {brand.suffix && <span className="ml-1 opacity-90" style={{ fontSize: "0.85em" }}>{brand.suffix}</span>}
    </span>
  );
}

function FormBadge({ brand, label }) {
  return (
    <span
      className="shrink-0 text-[0.58rem] font-bold uppercase tracking-widest px-2 py-1 rounded-md whitespace-nowrap"
      style={{ background: brand.accent, color: contrastText(brand.accent) }}
    >
      {label}
    </span>
  );
}

/* ---------- utilities ---------- */
function fontSizeFor(text, deltaRem = 0) {
  const len = (text || "").length;
  const base = len <= 6 ? 1.55 : len <= 9 ? 1.35 : len <= 12 ? 1.15 : len <= 16 ? 1.0 : 0.9;
  return `${base + deltaRem}rem`;
}

function withAlpha(hex, alpha) {
  const c = hex.replace("#", "");
  if (c.length !== 6) return hex;
  const r = parseInt(c.slice(0, 2), 16);
  const g = parseInt(c.slice(2, 4), 16);
  const b = parseInt(c.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function isLightColor(hex) {
  const c = hex.replace("#", "");
  if (c.length !== 6) return false;
  const r = parseInt(c.slice(0, 2), 16);
  const g = parseInt(c.slice(2, 4), 16);
  const b = parseInt(c.slice(4, 6), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 >= 180;
}

function contrastText(hex) {
  return isLightColor(hex) ? "#0f172a" : "#ffffff";
}

function darken(hex, amt = 20) {
  const c = hex.replace("#", "");
  if (c.length !== 6) return hex;
  const r = Math.max(0, parseInt(c.slice(0, 2), 16) - amt);
  const g = Math.max(0, parseInt(c.slice(2, 4), 16) - amt);
  const b = Math.max(0, parseInt(c.slice(4, 6), 16) - amt);
  return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
}
